import * as React from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  IconButton,
  Pagination,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useNavigate } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL;

interface Shop {
  shopId: number;
  name: string;
  address: string;
  cityId: number;
  city: { citiesId: number; name: string };
}

interface Manager {
  managerId: number;
  name: string;
  managerChatId: string | null;
  createdAt: string;
  shops: Shop[];
}

export default function Managers() {
  const navigate = useNavigate();
  const [managers, setManagers] = React.useState<Manager[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [pages, setPages] = React.useState(1);
  const limit = 20;

  const fetchManagers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/managers`, {
        params: { page, limit },
      });
      setManagers(data.data || []);
      setPages(data.pages || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchManagers();
  }, [page]);

  const handleDeleteManager = async (managerId: number) => {
    if (!confirm('Видалити менеджера?')) return;
    try {
      await axios.delete(`${API_URL}/managers/${managerId}`);
      fetchManagers();
    } catch (err: any) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleRemoveFromShop = async (shopId: number) => {
    if (!confirm('Відкріпити менеджера від цього магазину?')) return;
    try {
      await axios.put(`${API_URL}/remove-manager-from-shop/${shopId}`);
      fetchManagers();
    } catch (err: any) {
      alert(err.response?.data?.error || err.message);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography variant="h5">Менеджери</Typography>
        <Button variant="outlined" onClick={() => navigate('/managers/new')}>
          + Додати
        </Button>
      </Box>

      {loading ? (
        <Typography>Завантаження...</Typography>
      ) : managers.length === 0 ? (
        <Typography color="text.secondary">Немає менеджерів</Typography>
      ) : (
        managers.map((manager) => (
          <Paper key={manager.managerId} variant="outlined" sx={{ mb: 2 }}>
            {/* Верхня частина — менеджер */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontWeight={500}>{manager.name}</Typography>
                {manager.managerChatId ? (
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                    label="Telegram підключений"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                ) : (
                  <Chip label="Без Telegram" size="small" variant="outlined" />
                )}
              </Box>
              <Box>
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(`/managers/edit/${manager.managerId}`)
                  }
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteManager(manager.managerId)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Нижня частина — магазини */}
            {manager.shops.length === 0 ? (
              <Typography
                sx={{ px: 2, py: 1.5, fontSize: 13, color: 'text.secondary' }}
              >
                Немає прикріплених магазинів
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Назва</TableCell>
                      <TableCell>Адреса</TableCell>
                      <TableCell>Місто</TableCell>
                      <TableCell align="right">Дії</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {manager.shops.map((shop) => (
                      <TableRow key={shop.shopId} hover>
                        <TableCell>{shop.name}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {shop.address}
                        </TableCell>
                        <TableCell>
                          <Chip label={shop.city?.name ?? '—'} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(`/shops/edit/${shop.shopId}`)
                            }
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveFromShop(shop.shopId)}
                          >
                            <LinkOffIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {manager.shops.length > 0 && (
                      <Box
                        sx={{
                          px: 2,
                          py: 1,
                          borderTop: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="primary"
                          sx={{ cursor: 'pointer', display: 'inline' }}
                          onClick={() =>
                            navigate(`/managers/edit/${manager.managerId}`)
                          }
                        >
                          Показати всі магазини
                        </Typography>
                      </Box>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        ))
      )}

      {pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            page={page}
            count={pages}
            onChange={(_, v) => setPage(v)}
          />
        </Box>
      )}
    </Box>
  );
}
